import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Users2, MapPin, Clock, Calendar, ChevronRight, School, User } from 'lucide-react';
import { PremiumPageHeader, PageLoader } from '@repo/ui';

// ── helpers ──────────────────────────────────────────────────────────────────

/**
 * Parse a group name formatted as "C1 | AI Track | Tue & Sat | 6:30AM - 12:00PM | Branch"
 * Returns { cycle, track, rest[] }
 */
function parseGroupName(name: string) {
  const parts = (name ?? '')
    .split(' | ')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    const [cycle, track, ...rest] = parts;
    return { cycle, track, rest };
  }
  return { cycle: null, track: name, rest: [] };
}

const GroupsPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const navigate = useNavigate();

  const { data: groups, isLoading } = useQuery({
    queryKey: ['student-groups'],
    queryFn: () => api.get('/student/groups').then((res) => res.data?.data),
  });

  if (isLoading) {
    return <PageLoader message={t('common.loading')} />;
  }

  return (
    <div className="space-y-6">
      <PremiumPageHeader
        title={t('student.groups.title')}
        subtitle={t('student.groups.subtitle', {
          defaultValue: 'Explore your enrolled groups and tracks',
        })}
        icon={Users2}
        iconClassName="text-primary"
        iconContainerClassName="bg-primary/10 border-primary/20"
      />

      {!groups?.length ? (
        <div className="flex flex-col items-center justify-center py-24 rounded-3xl border-2 border-dashed border-muted/50 bg-muted/10 space-y-3">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
            <Users2 className="h-8 w-8 text-muted-foreground/30" />
          </div>
          <p className="font-bold text-muted-foreground">{t('student.groups.noGroups')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groups.map((group: any) => {
            const { cycle, track, rest } = parseGroupName(group.name);
            const instructorName = group.instructor?.userId?.name ?? group.instructor?.name ?? null;
            const mentorName = group.mentor?.userId?.name ?? group.mentor?.name ?? null;
            const branchName = group.branch
              ? isRtl
                ? (group.branch.nameAr ?? group.branch.nameEn)
                : (group.branch.nameEn ?? group.branch.nameAr)
              : null;

            // chips: days, time from rest[], branch
            const dayChip = rest[0] ?? null;
            const timeChip = rest[1] ?? null;

            return (
              <div
                key={group._id}
                onClick={() => navigate(`/groups/${group._id}`)}
                className="group relative rounded-2xl border border-border bg-card shadow-sm cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 hover:border-primary/30"
              >
                {/* ── Accent bar ── */}
                <div className="h-1 bg-primary w-full rounded-t-2xl" />

                <div className="p-5 space-y-3">
                  {/* ── Header row: cycle badge + track name + label pill ── */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      {cycle && (
                        <span className="shrink-0 px-2.5 py-1 rounded-lg bg-primary text-primary-foreground text-[11px] font-black tracking-wide">
                          {cycle}
                        </span>
                      )}
                      <h3 className="font-black text-base text-foreground leading-tight truncate">
                        {track}
                      </h3>
                    </div>
                    {group.label && (
                      <span className="shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary whitespace-nowrap border border-primary/20">
                        {group.label.name}
                      </span>
                    )}
                  </div>

                  {/* ── Group code ── */}
                  {group.code && (
                    <p className="text-xs font-mono text-muted-foreground/60 tracking-wider">
                      {group.code}
                    </p>
                  )}

                  {/* ── Info chips ── */}
                  {(dayChip || timeChip || branchName) && (
                    <div className="flex flex-wrap gap-1.5">
                      {dayChip && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary/8 text-primary text-xs font-medium border border-primary/10">
                          <Calendar className="h-3 w-3" />
                          {dayChip}
                        </span>
                      )}
                      {timeChip && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary/8 text-primary text-xs font-medium border border-primary/10">
                          <Clock className="h-3 w-3" />
                          {timeChip}
                        </span>
                      )}
                      {branchName && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary/8 text-primary text-xs font-medium border border-primary/10">
                          <MapPin className="h-3 w-3" />
                          {branchName}
                        </span>
                      )}
                    </div>
                  )}

                  {/* ── Staff row ── */}
                  {(instructorName || mentorName) && (
                    <div className="flex flex-wrap gap-4 pt-3 mt-1 border-t border-border">
                      {instructorName && (
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <School className="h-3.5 w-3.5 shrink-0" />
                          {instructorName}
                        </span>
                      )}
                      {mentorName && (
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <User className="h-3.5 w-3.5 shrink-0" />
                          {mentorName}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Arrow ── */}
                <div
                  className={`absolute bottom-4 ${isRtl ? 'left-4' : 'right-4'} h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300`}
                >
                  <ChevronRight className={`h-4 w-4 text-primary ${isRtl ? 'rotate-180' : ''}`} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GroupsPage;
